import type { BenefitType, SponsoringOfferDto } from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { BENEFIT_TYPE_LABELS } from '../../lib/format';
import { useConfirm } from '../ConfirmDialog';
import { OrgUnitSelect } from '../OrgUnitPicker';
import { Badge, Button, Dialog, Field, Input, Select, Textarea, useToast } from '../ui';
import { useSponsorCategories } from './common';

/** Werbemoeglichkeit anlegen oder bearbeiten. */
export function OfferDialog({ offer, onClose }: { offer: SponsoringOfferDto | null; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [title, setTitle] = useState(offer?.title ?? '');
  const [description, setDescription] = useState(offer?.description ?? '');
  const [benefitType, setBenefitType] = useState<string>(offer?.benefitType ?? '');
  const [unitId, setUnitId] = useState<string | null>(offer?.organisationUnit?.id ?? null);
  const [capacity, setCapacity] = useState(String(offer?.capacity ?? 1));
  const [sortOrder, setSortOrder] = useState(String(offer?.sortOrder ?? 0));

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['sponsoring-offers'] });
    await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title,
        description: description || null,
        benefitType: (benefitType || null) as BenefitType | null,
        organisationUnitId: unitId,
        capacity: Number(capacity),
        sortOrder: Number(sortOrder) || 0,
      };
      return offer
        ? api.patch(`/api/sponsoring-offers/${offer.id}`, payload)
        : api.post('/api/sponsoring-offers', payload);
    },
    onSuccess: async () => {
      await refresh();
      toast.show(offer ? 'Werbemöglichkeit gespeichert' : 'Werbemöglichkeit angelegt');
      onClose();
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  const archive = useMutation({
    mutationFn: () => api.patch(`/api/sponsoring-offers/${offer?.id}`, { archived: !offer?.archived }),
    onSuccess: async () => {
      await refresh();
      toast.show(offer?.archived ? 'Werbemöglichkeit wiederhergestellt' : 'Werbemöglichkeit archiviert');
      onClose();
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Ändern fehlgeschlagen', 'error'),
  });

  const archivieren = async (): Promise<void> => {
    if (!offer?.archived) {
      const ok = await confirm({
        title: 'Werbemöglichkeit archivieren?',
        message: `„${offer?.title ?? ''}“ wird danach nicht mehr zur Auswahl angeboten. Du kannst sie jederzeit wiederherstellen.`,
        confirmLabel: 'Ja, archivieren',
        tone: 'danger',
      });
      if (!ok) return;
    }
    archive.mutate();
  };

  const validCapacity = Number.isInteger(Number(capacity)) && Number(capacity) >= 1 && Number(capacity) <= 500;

  return (
    <Dialog
      open
      onClose={onClose}
      title={offer ? 'Werbemöglichkeit bearbeiten' : 'Neue Werbemöglichkeit'}
      footer={
        <>
          {offer && (
            <Button variant="secondary" onClick={() => void archivieren()} loading={archive.isPending}>
              {offer.archived ? 'Wiederherstellen' : 'Archivieren'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!title.trim() || !validCapacity}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {confirmDialog}
        <Field label="Bezeichnung" required>
          {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Bandenwerbung Sportplatz" />}
        </Field>
        <Field label="Beschreibung">
          {(id) => <Textarea id={id} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Art der Leistung" hint="Für die Zuordnung in Verträgen">
            {(id) => (
              <Select id={id} value={benefitType} onChange={(e) => setBenefitType(e.target.value)}>
                <option value="">– keine Angabe –</option>
                {Object.entries(BENEFIT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Bereich" hint="Leer lassen, wenn es den ganzen Verein betrifft">
            {(id) => <OrgUnitSelect id={id} value={unitId} onChange={setUnitId} allowEmpty emptyLabel="– ganzer Verein –" />}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Anzahl verfügbar" required hint="z. B. 20 Bandenplätze oder 13 Heimspiele">
            {(id) => <Input id={id} type="number" min={1} max={500} value={capacity} onChange={(e) => setCapacity(e.target.value)} />}
          </Field>
          <Field label="Reihenfolge">
            {(id) => <Input id={id} type="number" min={0} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/** Kategorien der Sponsoren pflegen - Bezeichnungen legt der Verein selbst fest. */
export function CategoriesDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const categories = useSponsorCategories(true);
  const [newName, setNewName] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['sponsor-categories'] });
    await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
  };
  const fail = (err: unknown): void =>
    toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error');

  const create = useMutation({
    mutationFn: () => api.post('/api/sponsor-categories', {
      name: newName.trim(),
      sortOrder: (categories.data?.length ?? 0),
    }),
    onSuccess: async () => {
      setNewName('');
      await refresh();
      toast.show('Kategorie angelegt');
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: (input: { id: string; name?: string; sortOrder?: number; archived?: boolean }) =>
      api.patch(`/api/sponsor-categories/${input.id}`, input),
    onSuccess: refresh,
    onError: fail,
  });

  const list = categories.data ?? [];
  const move = (index: number, direction: -1 | 1): void => {
    const other = list[index + direction];
    const current = list[index];
    if (!other || !current) return;
    update.mutate({ id: current.id, sortOrder: index + direction });
    update.mutate({ id: other.id, sortOrder: index });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Sponsor-Kategorien"
      footer={<Button variant="secondary" onClick={onClose}>Schließen</Button>}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Kategorien sind freiwillig, z. B. Hauptsponsor, Gold, Silber. Archivierte Kategorien bleiben an
          bestehenden Sponsoren stehen, werden aber nicht mehr angeboten.
        </p>
        <ul className="divide-y divide-slate-100">
          {list.map((category, index) => (
            <li key={category.id} className="flex flex-wrap items-center gap-2 py-2">
              <Input
                aria-label={`Bezeichnung ${category.name}`}
                className="min-w-0 flex-1"
                value={names[category.id] ?? category.name}
                onChange={(e) => setNames({ ...names, [category.id]: e.target.value })}
                onBlur={() => {
                  const next = (names[category.id] ?? category.name).trim();
                  if (next && next !== category.name) update.mutate({ id: category.id, name: next });
                }}
              />
              <span className="text-xs text-slate-500">{category.sponsorCount} Sponsor(en)</span>
              {category.archived && <Badge>archiviert</Badge>}
              <Button size="sm" variant="ghost" aria-label="nach oben" disabled={index === 0} onClick={() => move(index, -1)}>↑</Button>
              <Button size="sm" variant="ghost" aria-label="nach unten" disabled={index === list.length - 1} onClick={() => move(index, 1)}>↓</Button>
              <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: category.id, archived: !category.archived })}>
                {category.archived ? 'Wiederherstellen' : 'Archivieren'}
              </Button>
            </li>
          ))}
        </ul>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) create.mutate();
          }}
        >
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Neue Kategorie …" aria-label="Neue Kategorie" />
          <Button type="submit" variant="secondary" loading={create.isPending} disabled={!newName.trim()}>Hinzufügen</Button>
        </form>
      </div>
    </Dialog>
  );
}
